import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Button, Input, Card } from '../../components/ui'
import { Trophy, Sparkles, AlertCircle, CheckCircle, Upload, Shield, ArrowLeft, Lock, Check } from 'lucide-react'

export const PublicSignupPage = () => {
    const { tournamentId } = useParams()
    const [tournament, setTournament] = useState(null)
    const [loading, setLoading] = useState(true)
    const [teamName, setTeamName] = useState('')
    const [playersText, setPlayersText] = useState('')
    const [logoStyle, setLogoStyle] = useState('Modern - Strak & minimalistisch')
    const [generatedLogos, setGeneratedLogos] = useState([])
    const [selectedLogo, setSelectedLogo] = useState('')
    const [contactEmail, setContactEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [error, setError] = useState(null)
    const [logoLoading, setLogoLoading] = useState(false)
    const [logoMode, setLogoMode] = useState('logo')

    // Payment States
    const [isPaying, setIsPaying] = useState(false)

    const logoStyles = [
        'Modern - Strak & minimalistisch',
        'Klassiek - Traditioneel & tijdloos',
        'Speels - Kleurrijk & dynamisch',
        'Abstract - Geometrisch & uniek',
        'Vintage - Retro & nostalgisch'
    ]

    const avatarStyles = [
        'Modern - 3D Avatar (Pixar stijl)',
        'Realistisch - Portret',
        'Cartoon - Kleurrijk & Fun',
        'Anime - Manga stijl',
        'Retro - Pixel Art'
    ]

    const activeStyles = logoMode === 'logo' ? logoStyles : avatarStyles

    useEffect(() => {
        fetchTournamentInfo()
    }, [])

    const fetchTournamentInfo = async () => {
        try {
            const { data, error } = await supabase
                .from('tournaments')
                .select('*')
                .eq('id', tournamentId)
                .single()

            if (error) throw error
            setTournament(data)
        } catch (err) {
            setError('Toernooi niet gevonden of ongeldige link.')
        } finally {
            setLoading(false)
        }
    }

    const playSuccessSound = () => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const now = ctx.currentTime;
            
            // Play an arpeggio C-major chord
            const notes = [261.63, 329.63, 392.00, 523.25, 659.25]; // C4, E4, G4, C5, E5
            notes.forEach((freq, idx) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(freq, now + idx * 0.08);
                
                gain.gain.setValueAtTime(0, now + idx * 0.08);
                gain.gain.linearRampToValueAtTime(0.15, now + idx * 0.08 + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.08 + 0.6);
                
                osc.connect(gain);
                gain.connect(ctx.destination);
                
                osc.start(now + idx * 0.08);
                osc.stop(now + idx * 0.08 + 0.7);
            });
        } catch (e) {
            console.error(e);
        }
    }

    const generateLogo = async () => {
        if (!teamName) return
        setLogoLoading(true)
        setGeneratedLogos([])

        try {
            const seed = Date.now()
            let prompt = ''

            if (logoMode === 'logo') {
                const styleClean = logoStyle.split(' - ')[0].toLowerCase()
                prompt = encodeURIComponent(`${styleClean} sports team logo for "${teamName}", minimal, vector style, white background, high quality, professional design`)
            } else {
                const styleClean = logoStyle.split(' - ')[0].toLowerCase()
                prompt = encodeURIComponent(`${styleClean} avatar of a sports player for "${teamName}", character portrait, white background, high quality, ${styleClean === 'anime' ? 'anime style' : '3d render'}`)
            }

            const url = `https://image.pollinations.ai/prompt/${prompt}?width=512&height=512&seed=${seed}&nologo=true`

            setGeneratedLogos([{ id: 1, url }])
            setSelectedLogo(url)
        } catch (err) {
            console.error('AI Gen Error:', err)
        } finally {
            setLogoLoading(false)
        }
    }

    const handleImageUpload = async (e) => {
        const file = e.target.files[0]
        if (!file) return

        if (file.size > 2 * 1024 * 1024) {
            alert('Afbeelding is te groot (max 2MB)')
            return
        }

        setLogoLoading(true)
        try {
            const fileExt = file.name.split('.').pop()
            const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`
            const filePath = `${fileName}`

            const { error: uploadError } = await supabase.storage
                .from('team-logos')
                .upload(filePath, file)

            if (uploadError) throw uploadError

            const { data: { publicUrl } } = supabase.storage
                .from('team-logos')
                .getPublicUrl(filePath)

            setSelectedLogo(publicUrl)
        } catch (error) {
            console.error('Error uploading image:', error)
            alert('Kon afbeelding niet uploaden. Probeer een URL te plakken of AI te gebruiken.')
        } finally {
            setLogoLoading(false)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!teamName) return

        // If paid signup is enabled, redirect to real Stripe Checkout
        if (tournament.payment_enabled && Number(tournament.entry_fee) > 0) {
            await handleStripeCheckout()
            return
        }

        // Direct registration for free signups
        await executeRegistration('free', 0, 'none')
    }

    const executeRegistration = async (paymentStatus = 'free', paymentAmount = 0, paymentIntentId = 'none') => {
        setIsSubmitting(true)
        try {
            // 1. Create Team
            const { data: team, error: teamError } = await supabase
                .from('teams')
                .insert({
                    tournament_id: tournamentId,
                    name: teamName,
                    logo_url: selectedLogo,
                    contact_email: contactEmail,
                    payment_status: paymentStatus,
                    payment_amount: paymentAmount,
                    payment_intent_id: paymentIntentId
                })
                .select()
                .single()

            if (teamError) throw teamError

            // 2. Add Players if provided
            const playerNames = playersText.split('\n').map(p => p.trim()).filter(p => p.length > 0)

            if (playerNames.length > 0) {
                const playersToInsert = playerNames.map(name => ({
                    tournament_id: tournamentId,
                    team_id: team.id,
                    name: name
                }))

                const { error: playersError } = await supabase
                    .from('players')
                    .insert(playersToInsert)

                if (playersError) {
                    console.error('Error adding players:', playersError)
                }
            }

            setSubmitted(true)
        } catch (err) {
            setError(err.message)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleStripeCheckout = async () => {
        setIsPaying(true)
        try {
            const { data, error } = await supabase.functions.invoke('create-checkout', {
                body: {
                    priceId: tournament.payment_settings?.stripe_price_id,
                    userId: (await supabase.auth.getUser()).data.user?.id || 'guest_' + Date.now(),
                    tenantId: tournament.tenant_id || '',
                    mode: 'payment',
                    successUrl: `${window.location.origin}/signup/${tournamentId}?payment=success`,
                    cancelUrl: `${window.location.origin}/signup/${tournamentId}?payment=cancelled`,
                }
            })
            if (error || !data?.url) {
                // Fallback: als er geen Stripe price ID is ingesteld, direct registreren
                await executeRegistration('pending', Number(tournament.entry_fee), 'manual_' + Date.now())
            } else {
                window.location.href = data.url
            }
        } catch (err) {
            console.error('Checkout error:', err)
            alert('Fout bij starten betaling. Probeer opnieuw.')
        } finally {
            setIsPaying(false)
        }
    }

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div></div>

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <Card className="max-w-md w-full text-center p-8">
                <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
                <h2 className="text-xl font-bold mb-2">Oeps!</h2>
                <p className="text-gray-600">{error}</p>
            </Card>
        </div>
    )

    if (submitted) return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-500 to-pink-600 p-4">
            <Card className="max-w-md w-full text-center p-12 shadow-2xl animate-fade-in bg-white">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                    <CheckCircle className="text-green-600" size={40} />
                </div>
                <h2 className="text-3xl font-bold mb-4 text-gray-900">Ingeschreven!</h2>
                <p className="text-gray-600 text-lg mb-8">
                    Jullie team <strong>{teamName}</strong> is succesvol aangemeld voor <em>{tournament.name}</em>.
                </p>
                {tournament.entry_fee > 0 && tournament.payment_enabled && (
                    <div className="p-4 bg-emerald-50 text-emerald-800 rounded-xl border border-emerald-100 mb-6 flex items-center justify-center gap-2">
                        <Shield size={18} />
                        <span className="font-medium text-sm">Betaling van €{Number(tournament.entry_fee).toFixed(2)} succesvol voldaan.</span>
                    </div>
                )}
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-sm text-gray-500">De organisatie neemt binnenkort contact op of je ziet je team verschijnen in de app.</p>
                </div>
            </Card>
        </div>
    )

    // NOTE: Fake checkout removed - now using real Stripe Checkout redirect
    if (false) return (
        <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4 font-sans">
            <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
                {/* Header */}
                <div className="p-6 border-b border-slate-800 bg-slate-900/50 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-500/10 text-emerald-400 rounded-lg border border-emerald-500/20">
                            <Lock size={18} className="animate-pulse" />
                        </div>
                        <div>
                            <span className="font-bold text-sm block">Veilige Betaling</span>
                            <span className="text-xs text-slate-400">via Mollie secure gateway</span>
                        </div>
                    </div>
                    <button 
                        onClick={() => setShowCheckout(false)}
                        className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-all"
                    >
                        <ArrowLeft size={18} />
                    </button>
                </div>

                {/* Main Content */}
                <div className="p-6 space-y-6">
                    {/* Summary box */}
                    <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-1.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-bold rounded-bl-xl border-l border-b border-emerald-500/10">TESTOMGEVING</div>
                        <span className="text-slate-400 text-xs uppercase tracking-wider font-bold">Te betalen bedrag</span>
                        <div className="text-4xl font-extrabold text-white mt-1">€{Number(tournament.entry_fee).toFixed(2)}</div>
                        <p className="text-slate-400 text-xs mt-2 font-mono">Inschrijfgeld - {tournament.name}</p>
                    </div>

                    {/* Method Selector */}
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Betaalmethode</label>
                        <div className="grid grid-cols-2 gap-3">
                            {tournament.payment_settings?.payment_methods?.includes('ideal') && (
                                <button
                                    onClick={() => setPaymentMethod('ideal')}
                                    className={`p-3.5 rounded-xl border text-sm font-bold flex items-center justify-center gap-2 transition-all ${
                                        paymentMethod === 'ideal' 
                                            ? 'bg-slate-800 border-emerald-500 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.15)]' 
                                            : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                                    }`}
                                >
                                    🏦 iDEAL
                                </button>
                            )}
                            {tournament.payment_settings?.payment_methods?.includes('creditcard') && (
                                <button
                                    onClick={() => setPaymentMethod('creditcard')}
                                    className={`p-3.5 rounded-xl border text-sm font-bold flex items-center justify-center gap-2 transition-all ${
                                        paymentMethod === 'creditcard' 
                                            ? 'bg-slate-800 border-emerald-500 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.15)]' 
                                            : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                                    }`}
                                >
                                    💳 Creditcard
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Specific details based on method */}
                    {paymentMethod === 'ideal' ? (
                        <div className="space-y-3 animate-fadeIn">
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Kies je bank</label>
                            <select 
                                value={selectedBank}
                                onChange={(e) => setSelectedBank(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:border-emerald-500 outline-none text-sm transition-all"
                            >
                                <option value="Rabobank">Rabobank</option>
                                <option value="ING">ING</option>
                                <option value="ABN AMRO">ABN AMRO</option>
                                <option value="SNS Bank">SNS Bank</option>
                                <option value="RegioBank">RegioBank</option>
                                <option value="Bunq">Bunq</option>
                            </select>
                        </div>
                    ) : (
                        <div className="space-y-4 animate-fadeIn">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Naam Kaarthouder</label>
                                <input
                                    type="text"
                                    placeholder="J. de Vries"
                                    value={cardName}
                                    onChange={(e) => setCardName(e.target.value)}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white focus:border-emerald-500 outline-none text-sm transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Kaartnummer</label>
                                <input
                                    type="text"
                                    maxLength={16}
                                    placeholder="4111 2222 3333 4444"
                                    value={cardNumber}
                                    onChange={(e) => setCardNumber(e.target.value)}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white focus:border-emerald-500 outline-none text-sm transition-all font-mono"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Vervaldatum</label>
                                    <input
                                        type="text"
                                        placeholder="MM/JJ"
                                        maxLength={5}
                                        value={cardExpiry}
                                        onChange={(e) => setCardExpiry(e.target.value)}
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white focus:border-emerald-500 outline-none text-sm transition-all font-mono"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">CVC</label>
                                    <input
                                        type="password"
                                        maxLength={3}
                                        placeholder="123"
                                        value={cardCvc}
                                        onChange={(e) => setCardCvc(e.target.value)}
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white focus:border-emerald-500 outline-none text-sm transition-all font-mono"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Pay Button */}
                    <div className="pt-2">
                        {paymentSuccess ? (
                            <div className="w-full py-4 bg-emerald-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(16,185,129,0.4)] animate-bounce">
                                <Check size={20} />
                                Betaling Geslaagd!
                            </div>
                        ) : (
                            <button
                                onClick={handleSimulatePayment}
                                disabled={isPaying}
                                className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-slate-950 rounded-xl font-extrabold text-base flex items-center justify-center gap-2 transition-all shadow-[0_10px_20px_rgba(16,185,129,0.25)] hover:shadow-[0_10px_25px_rgba(16,185,129,0.35)] active:scale-[0.98] disabled:opacity-50"
                            >
                                {isPaying ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin"></div>
                                        Bezig met verwerken...
                                    </>
                                ) : (
                                    <>
                                        Nu betalen met {paymentMethod === 'ideal' ? `iDEAL (${selectedBank})` : 'Creditcard'} 💳
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {/* Footer security badge */}
                <div className="p-4 bg-slate-950 text-center border-t border-slate-800/50 flex items-center justify-center gap-2 text-[10px] text-slate-400">
                    <Shield size={12} className="text-emerald-500" />
                    100% Veilige 256-bit SSL gecodeerde verbinding
                </div>
            </div>
        </div>
    )

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
            <div className="max-w-xl w-full space-y-8">
                <div className="text-center">
                    <div className="mx-auto h-24 w-24 bg-gradient-to-br from-orange-400 to-pink-500 rounded-2xl shadow-xl flex items-center justify-center mb-6 transform -rotate-6">
                        <Trophy className="text-white h-12 w-12" />
                    </div>
                    <h2 className="text-4xl font-extrabold text-white tracking-tight mb-2">
                        Doe mee!
                    </h2>
                    <p className="text-lg text-gray-300">
                        Schrijf je team in voor <strong>{tournament.name}</strong>
                    </p>
                </div>

                <Card className="p-8 shadow-2xl bg-white/95 backdrop-blur border-0">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Team Naam</label>
                            <Input
                                value={teamName}
                                onChange={(e) => setTeamName(e.target.value)}
                                placeholder="bijv. De Kampioenen"
                                className="text-lg py-3"
                                required
                                autoFocus
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Spelers (Optioneel)</label>
                            <textarea
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none text-gray-700 min-h-[120px]"
                                placeholder="Jan de Vries&#10;Piet Jansen&#10;Klaas Vaak&#10;(Eén naam per regel)"
                                value={playersText}
                                onChange={(e) => setPlayersText(e.target.value)}
                            />
                            <p className="text-xs text-gray-500 mt-2">Zet elke speler op een nieuwe regel.</p>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Team Logo (Optioneel)</label>
                            <div className="space-y-4 mb-6">
                                {/* Mode Toggle */}
                                <div className="flex bg-gray-100 p-1 rounded-lg">
                                    <button
                                        type="button"
                                        onClick={() => { setLogoMode('logo'); setLogoStyle(logoStyles[0]); }}
                                        className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${logoMode === 'logo' ? 'bg-white shadow text-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        🛡️ Team Logo
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setLogoMode('avatar'); setLogoStyle(avatarStyles[0]); }}
                                        className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${logoMode === 'avatar' ? 'bg-white shadow text-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        👤 Speler Avatar
                                    </button>
                                </div>

                                {/* Style Selector */}
                                <select
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white"
                                    value={logoStyle}
                                    onChange={(e) => setLogoStyle(e.target.value)}
                                >
                                    {activeStyles.map(style => (
                                        <option key={style} value={style}>{style}</option>
                                    ))}
                                </select>

                                {/* Controls */}
                                <div className="flex gap-3">
                                    <div className="flex-1">
                                        <input
                                            type="text"
                                            placeholder="Plak afbeeldings URL..."
                                            value={selectedLogo}
                                            onChange={(e) => setSelectedLogo(e.target.value)}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none text-sm"
                                        />
                                    </div>
                                    <label className="cursor-pointer">
                                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                                        <div className="flex items-center justify-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-300 transition-colors h-full" title="Upload afbeelding">
                                            <Upload size={18} />
                                        </div>
                                    </label>
                                    <Button type="button" onClick={generateLogo} disabled={!teamName || logoLoading} variant="secondary" className="whitespace-nowrap">
                                        <Sparkles size={16} className="mr-2" />
                                        {logoLoading ? '...' : 'Genereer'}
                                    </Button>
                                </div>
                            </div>

                            {selectedLogo && (
                                <div className="flex justify-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                                    <img src={selectedLogo} alt="Preview" className="w-32 h-32 object-contain rounded-lg shadow-sm bg-white" />
                                </div>
                            )}
                        </div>

                        <div className="pt-4 border-t border-gray-100">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Email Contactpersoon</label>
                            <Input
                                type="email"
                                value={contactEmail}
                                onChange={(e) => setContactEmail(e.target.value)}
                                placeholder="jouw@email.com"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-2">Wordt alleen gebruikt voor communicatie over dit toernooi.</p>
                        </div>

                        <Button type="submit" disabled={isSubmitting} className="w-full text-lg py-6 bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 transform transition hover:scale-[1.02] shadow-lg">
                            {isSubmitting ? 'Bezig met inschrijven...' : tournament?.payment_enabled && Number(tournament?.entry_fee) > 0 ? `Nu Inschrijven & Betalen (€${Number(tournament.entry_fee).toFixed(2)}) 🚀` : 'Nu Inschrijven 🚀'}
                        </Button>
                    </form>
                </Card>

                <p className="text-center text-gray-400 text-sm">
                    Powered by ProPoule
                </p>
            </div>
        </div>
    )
}
